import React, { createContext, useContext, ReactNode } from 'react';
import { MediaResource } from '@brightmls/nextgen.lae.mediacenter';

/**
 * Media job from get-listing response.
 * Represents an in-progress or recently completed upload job.
 */
export interface MediaJob {
  jobId: string;
  fileName: string;
  fileSize?: number;
  category: string;
  status:
    | 'pending'
    | 'queued'
    | 'processing'
    | 'submitting'
    | 'submitted'
    | 'completed'
    | 'failed';
  aiStatus?:
    | 'pending'
    | 'scanning'
    | 'scanned'
    | 'applying'
    | 'applied'
    | 'failed'
    | 'skipped';
  failedStep?: 'submission' | 'scan' | 'apply';
  error?: string;
  resultUrl?: string;
  previewUrl?: string;
  processedUrl?: string;
  mediaKey?: string;
  mediaItemNumber?: number;
  mediaDisplayOrder?: number;
  imageMetadata?: {
    width?: number;
    height?: number;
    format?: string;
    size?: number;
  };
  pdfMetadata?: {
    pageCount?: number;
    size?: number;
  };
  createdAt: string;
  updatedAt?: string;
  processingCompletedAt?: string;
}

interface MediaJobsContextValue {
  /** Initial media jobs from get-listing response */
  jobs: MediaJob[];
  media: MediaResource[];
}

const MediaJobsContext = createContext<MediaJobsContextValue | null>(null);

interface MediaJobsProviderProps {
  children: ReactNode;
  jobs: MediaJob[];
  media: MediaResource[];
}

/**
 * Provider for initial media jobs from the listing load.
 * Wraps the edit route to make jobs available to MediaCenterDataLoader.
 */
export const MediaJobsProvider: React.FC<MediaJobsProviderProps> = ({
  children,
  jobs,
  media,
}) => {
  return (
    <MediaJobsContext.Provider value={{ jobs, media }}>
      {children}
    </MediaJobsContext.Provider>
  );
};

/**
 * Hook to access initial media jobs from the listing load.
 * Returns empty array if not within a MediaJobsProvider.
 */
export const useMediaJobs = (): {
  initialMediaJobs: MediaJob[];
  initialMedia: MediaResource[];
} => {
  const context = useContext(MediaJobsContext);
  return {
    initialMediaJobs: context?.jobs ?? [],
    initialMedia: context?.media ?? [],
  };
};
